import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { CanvasNode, CanvasNodeData } from "../../models/CanvasNode";
import { useFlowStore } from "../../store/FlowStateStore.ts";
import LinkHandler from "./LinkHandler.tsx";
import { Environment as environment } from "../../environment.ts";

interface TagInter {
    tagId: string;
    tagName: string;
    businessId: string;
}

const Tag: React.FC<any> = (data) => {

    // Ids of the tags this node applies. Previously the node saved the *entire* fetched tag list
    // and the dropdown selection was never persisted, so the node meant "apply every tag this
    // business has" rather than the one that was picked.
    const [selectedTagIds, setSelectedTagIds] = useState<string[]>([""]);
    const [tags, setTags] = useState<TagInter[]>([]);
    const { nodes, setNodes, businessId } = useFlowStore();
    const hasRestoredRef = useRef(false);
    const isFirstRunRef = useRef(true);

    const getAllTags = () => {
        const payload = {
            businessId: businessId,
            page: 0,
            size: 100
        }
        axios.put(`${environment.baseUrl}/getAll/tag`, payload).then((response) => {
            const list: TagInter[] = response.data.data.data ?? [];
            setTags(list);
            // Don't overwrite a restored selection with the first fetched tag.
            if (!hasRestoredRef.current && list.length > 0) {
                setSelectedTagIds((current) =>
                    current.length === 1 && !current[0] ? [list[0].tagId] : current
                );
            }
        }).catch(() => { });
    }

    // Restore what was saved on this node. Accepts `_id` as well as `tagId`: an embedded tag is
    // persisted under `_id` because tagId is its @Id on the backend.
    useEffect(() => {
        const node = nodes.find((n) => n.data.id === data.nodeId);
        if (node) {
            const content = (node.data as CanvasNodeData).content.find((c) => c.id === data.subNodeId);
            const savedTags = (content?.data as any)?.tags;
            if (Array.isArray(savedTags) && savedTags.length > 0) {
                const ids = savedTags
                    .map((tag: any) => tag?.tagId ?? tag?._id)
                    .filter((id: any) => !!id)
                    .map((id: any) => String(id));
                if (ids.length > 0) {
                    setSelectedTagIds(ids);
                    hasRestoredRef.current = true;
                }
            }
        }
    }, []);

    useEffect(() => {
        getAllTags();
    }, []);

    useEffect(() => {
        // Skip the first run: it fires with pristine state, before the restore effect above has
        // flowed through a re-render, and would persist blanks over a just-restored selection.
        if (isFirstRunRef.current) {
            isFirstRunRef.current = false;
            return;
        }

        // Persist only the chosen tags, in full, so the backend can resolve them by id.
        const chosenTags = selectedTagIds
            .filter((id) => !!id)
            .map((id) => tags.find((tag) => tag.tagId === id))
            .filter((tag): tag is TagInter => !!tag);

        const updatedNodes = nodes.map((node) => {
            if (node.data.id == data.nodeId) {
                return {
                    ...node,
                    data: {
                        ...node.data,
                        content: [...(node.data as CanvasNodeData).content.map((nodeContent) => {
                            if (nodeContent.id == data.subNodeId) {
                                return {
                                    ...nodeContent,
                                    data: {
                                        tags: chosenTags
                                    }
                                }
                            }
                            return nodeContent
                        })]
                    }
                }
            }
            return node;
        });
        setNodes([...(updatedNodes as CanvasNode[])]);
    }, [selectedTagIds, tags]);

    const setTagValue = (index: number, value: string) => {
        // The old version returned `value` from both branches, so editing one row overwrote them all.
        setSelectedTagIds(selectedTagIds.map((tagId, idx) => (idx === index ? value : tagId)));
    }

    const removeTag = (index: number) => {
        const remaining = selectedTagIds.filter((_, idx) => (idx != index));
        setSelectedTagIds(remaining.length > 0 ? remaining : [""]);
    }

    // Tags already chosen in another row, so the same tag can't be added twice.
    const isTakenElsewhere = (tagId: string, index: number) =>
        selectedTagIds.some((selected, idx) => idx !== index && selected === tagId);

    return (
        <div className="node-body">
            {
                selectedTagIds.length > 0 &&
                selectedTagIds.map((tagId, idx) => {
                    return (
                        <div key={idx}>
                            <div className="flex flex-row justify-between">
                                <span className="font-label">Select Tag</span>
                                <img src={require("../../assets/node/close.png")} style={{ height: '15px', width: '15px' }} onClick={() => removeTag(idx)} />
                            </div>
                            <select value={tagId} onChange={(event) => setTagValue(idx, event.target.value)}>
                                <option value="">Select a tag</option>
                                {
                                    tags.map((item) => {
                                        return <option
                                            key={item.tagId}
                                            value={item.tagId}
                                            disabled={isTakenElsewhere(item.tagId, idx)}>
                                            {item.tagName}
                                        </option>
                                    })
                                }
                            </select>
                        </div>
                    )
                })
            }
            <button onClick={() => { setSelectedTagIds([...selectedTagIds, ""]) }} className="font-label">+ Add Tag</button>
            <LinkHandler />
        </div>
    )
}

export default Tag;
